import React, { useState, useEffect, useRef } from 'react'
import Modal from '@/v2/components/common/Modal'
import validator from '@/v2/components/forms/TimeOff/validator'
import TimeOffForm from '@/v2/components/forms/TimeOff/Form'
import formatTimeOffToForm from '@/v2/components/forms/TimeOff/toForm'
import fromForm from '@/v2/components/forms/TimeOff/fromForm'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { errorsFromJson } from '@/v2/utils/forms'

// Deep equality check function
const isEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2)
}
import { useLoadData } from './hooks/useLoadData'
import { useUpdateTimeOff, useRemoveTimeOff } from '@/v2/hooks/useTimeOffs'

const EditDeleteTimeOff = ({ id, timeoff, isOpen, onClose }) => {
  const { loading, rooms, doctors, clinic } = useLoadData(id)
  const { updateTimeOff } = useUpdateTimeOff(id)
  const { removeTimeOff } = useRemoveTimeOff(id)
  const [values, setValues] = useState(formatTimeOffToForm(timeoff))
  const [formError, setFormError] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const { onOpenSnackbar } = useSnackbarContext()
  const isMounted = useRef(true)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const handleChange = value => {
    if (isMounted.current) {
      setValues({ ...values, ...value })
    }
  }

  const handleSubmit = async () => {
    const validErrors = validator(values)
    if (isMounted.current) {
      setFormError(validErrors || {})
    }

    if (!validErrors) {
      if (isMounted.current) {
        setIsLoading(true)
      }
      try {
        const payload = fromForm(values)
        const response = await updateTimeOff(payload)
        if (isMounted.current) {
          onOpenSnackbar('Updated blocked time.')
          onClose()
        }
      } catch (error) {
        console.error('updateTimeOff error:', error)
        if (isMounted.current) {
          // Try to parse structured errors first from response
          const asyncErrors = error.response?.errors
            ? errorsFromJson(error.response.errors)
            : errorsFromJson(error.message)

          if (asyncErrors) {
            setFormError({ ...asyncErrors })
          } else {
            // Show user-friendly error message
            const errorMessage = error.message || 'Something went wrong!'
            onOpenSnackbar(errorMessage, 'error')
          }
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false)
        }
      }
    }
  }

  const handleDelete = async () => {
    if (isMounted.current) {
      setIsLoading(true)
    }
    try {
      await removeTimeOff()
      if (isMounted.current) {
        onOpenSnackbar('Blocked time canceled.')
        onClose()
      }
    } catch (error) {
      console.error('removeTimeOff error:', error)
      if (isMounted.current) {
        onOpenSnackbar('Something went wrong!', 'error')
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false)
      }
    }
  }

  const handleCancel = () => {
    if (confirm('Are you sure you want to cancel this blocked time?')) {
      handleDelete()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      loading={isLoading || loading}
      title="Edit Blocked Time"
      cancelLabel="Cancel Blocked Time"
      onConfirm={handleSubmit}
      onClose={({ header, footer }) => {
        if (header) {
          if (!isEqual(formatTimeOffToForm(timeoff), values)) {
            const leave = confirm(
              'Are you sure you want to leave without saving changes?'
            )
            if (leave) onClose()
          } else {
            onClose()
          }
        }
        if (footer) {
          handleCancel()
        }
      }}
    >
      <TimeOffForm
        values={values}
        clinic={clinic}
        rooms={rooms}
        doctors={doctors}
        error={formError}
        onChange={handleChange}
      />
    </Modal>
  )
}

export default EditDeleteTimeOff
